/**
 * Durable in-memory drafts for per-page Fabric overlays.
 * Survives page-switch races that can drop zustand commits.
 */

import type { FabricJson } from '@/lib/pdf/pdfAnnotations';

const drafts = new Map<number, FabricJson>();

function objectCount(json: FabricJson | null | undefined): number {
  if (!json || !Array.isArray(json.objects)) return 0;
  return json.objects.length;
}

export function setAnnotationDraft(pageIndex: number, json: FabricJson): void {
  if (!Number.isFinite(pageIndex)) return;
  drafts.set(pageIndex, json);
}

export function getAnnotationDraft(pageIndex: number): FabricJson | undefined {
  return drafts.get(pageIndex);
}

export function clearAnnotationDrafts(): void {
  drafts.clear();
}

/** Merge zustand map + drafts; prefer the side with more objects per page. */
export function mergeAnnotationDrafts(
  storeMap: Record<number, FabricJson>
): Record<number, FabricJson> {
  const out: Record<number, FabricJson> = {};

  for (const [key, json] of Object.entries(storeMap)) {
    const idx = Number(key);
    if (!Number.isFinite(idx) || !json) continue;
    out[idx] = json;
  }

  for (const [idx, draft] of drafts) {
    const existing = out[idx];
    // Prefer richer overlay — never let a thinner draft wipe a fuller store page
    if (!existing || objectCount(draft) > objectCount(existing)) {
      out[idx] = draft;
    }
  }

  return out;
}
